import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import type { SitrepSectionType } from 'components/DisasterGPT/hooks/useSitrepsV2';

dayjs.extend(utc);
dayjs.extend(timezone);

type SectionVersion = SitrepSectionType['section_versions'][number];

type SectionLike = {
  type?: string;
  section_versions?: SectionVersion[] | null;
};

export type SectionLastUpdated = {
  /** Raw timestamp of the version the label describes. */
  timestamp: string;
  /** Formatted to match the report card label. */
  formattedTime: string;
  /** Who saved the version, or null when it cannot be attributed. */
  authorName: string | null;
  label: string;
};

/**
 * Statuses where the version holds finished content rather than a run in
 * progress.
 */
const SETTLED_STATUSES = ['completed', 'summarized'];

const isSettled = (version: SectionVersion): boolean =>
  SETTLED_STATUSES.includes((version.status || '').toLowerCase());

/** Z or a numeric offset such as +00:00 or -0400. */
const HAS_EXPLICIT_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/**
 * The version column is timestamp without time zone and is written in UTC. A
 * value with no offset is that UTC clock; a value that already carries Z or an
 * offset is that instant. Either way the label is the browser's zone.
 */
export const formatInBrowserTimeZone = (timestamp: string): string | null => {
  const trimmed = timestamp.trim();
  const parsed = HAS_EXPLICIT_OFFSET.test(trimmed)
    ? dayjs(trimmed)
    : dayjs.utc(trimmed);
  if (!parsed.isValid()) return null;

  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return parsed.tz(browserZone).format('MM/DD/YYYY HH:mm');
};

/**
 * Describes when a single report section last changed. Skips versions for runs
 * that are still in flight or awaiting review so the label always matches the
 * content on screen, which means a generating section keeps showing the time of
 * its last good result.
 */
export const describeSectionLastUpdated = (
  section: SectionLike
): SectionLastUpdated | null => {
  // Versions arrive newest-first (ORDER BY timestamp DESC in getSitrepById).
  const version = (section.section_versions ?? []).find(isSettled);
  if (!version?.timestamp) return null;

  const formattedTime = formatInBrowserTimeZone(version.timestamp);
  if (!formattedTime) return null;

  const savedBy = version.updated_by_user_name?.trim();
  const authorName = savedBy || null;
  return {
    timestamp: version.timestamp,
    formattedTime,
    authorName,
    label: authorName
      ? `Updated ${formattedTime} by ${authorName}`
      : `Updated ${formattedTime}`,
  };
};
